import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaSearch, FaChevronRight, FaChevronDown, FaComments, FaEnvelope, FaPhone, FaThumbsUp, FaHeadset
} from 'react-icons/fa';
import {
  fetchHelpCategories,
  searchHelpArticles,
  submitFeedback,
} from '../../../redux/settingsPages/helpcenter/helpcenterActions';
import { clearSearch, clearHelpError } from '../../../redux/settingsPages/helpcenter/helpcenterSlice';

const HelpCenterPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    categories,
    searchResults,
    loading,
    error,
    lastQuery,
    feedbackStatus,
  } = useSelector((state) => state.helpcenter);

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  // Fetch categories on mount
  useEffect(() => {
    if (categories.length === 0) dispatch(fetchHelpCategories());
  }, [dispatch, categories.length]);

  // Search when query changes (debounced by the thunk condition)
  useEffect(() => {
    if (searchQuery.trim()) {
      const timer = setTimeout(() => {
        dispatch(searchHelpArticles(searchQuery));
      }, 300);
      return () => clearTimeout(timer);
    } else {
      dispatch(clearSearch());
    }
  }, [searchQuery, dispatch]);

  const handleSearch = (e) => setSearchQuery(e.target.value);

  const handleFeedback = (helpful) => {
    dispatch(submitFeedback({ helpful, query: lastQuery }))
      .unwrap()
      .then(() => {
        if (helpful) {
          alert('Thanks for the feedback! 😊');
        } else {
          alert('We\'re sorry this wasn\'t helpful. Our support team will reach out soon.');
        }
      })
      .catch(() => {
        alert('Failed to submit feedback. Please try again.');
      });
  };

  const contactOptions = [
    {
      id: 'chat',
      title: 'Live Chat',
      description: 'Get instant help from our support team',
      icon: FaComments,
      availability: '24/7',
      color: 'bg-green-600'
    },
    {
      id: 'email',
      title: 'Email Support',
      description: 'Send us a detailed message',
      icon: FaEnvelope,
      availability: 'Response within 24 hours',
      color: 'bg-blue-600'
    },
    {
      id: 'phone',
      title: 'Phone Support',
      description: 'Speak directly with our team',
      icon: FaPhone,
      availability: 'Mon–Fri 8AM–8PM ET',
      color: 'bg-violet-700'
    }
  ];

  const faqItems = [
    {
      id: 1,
      question: 'How do I reset my password?',
      answer: 'Click "Forgot Password" on the login page and follow the instructions sent to your email. The reset link is valid for 30 minutes.'
    },
    {
      id: 2,
      question: 'What are the trading fees?',
      answer: 'Stock trading is commission-free. Options contracts are $0.65 per contract. Check our pricing page for complete details.'
    },
    {
      id: 3,
      question: 'How do I enable two-factor authentication?',
      answer: 'Go to Settings → Security → Two-Factor Authentication and follow the setup steps. We recommend using an authenticator app for best security.'
    },
    {
      id: 4,
      question: 'Can I trade outside market hours?',
      answer: 'Yes, extended hours trading is available 4:00 AM–8:00 PM ET. Note that liquidity and pricing may differ from regular market hours.'
    },
    {
      id: 5,
      question: 'How do I export my data?',
      answer: 'Go to Settings → Data & Privacy → Data Management and click "Download My Data". You\'ll receive an email with a download link within 24 hours.'
    },
    {
      id: 6,
      question: 'How do I delete my account?',
      answer: 'Go to Settings → Data & Privacy → Data Management and click "Delete Account". There is a 30-day grace period during which you can cancel the deletion.'
    }
  ];

  const displayCategories = searchQuery.trim() ? searchResults : categories;

  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">{t('helpCenter.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {t('helpCenter.subtitle')}
        </p>
        <div className="max-w-2xl mx-auto relative">
          <input
            type="text"
            placeholder={t('helpCenter.search')}
            // placeholder:truncate -- the full placeholder ("Search for help
            // articles, guides, or FAQs...") hard-cut mid-word against the
            // input's right edge on narrow screens with no ellipsis (found
            // live during the 2026-07-17 mobile audit); this gives it a
            // clean ellipsis instead, without shortening the copy for
            // wider screens where it already fits fine.
            className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg pl-12 pr-4 py-4 focus:ring-2 focus:ring-blue-500 focus:outline-none text-lg shadow-sm placeholder:truncate"
            value={searchQuery}
            onChange={handleSearch}
          />
          <FaSearch className="absolute left-4 top-5 text-gray-400 text-xl" />
          {loading && (
            <div className="absolute right-4 top-5">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto space-y-8">
        {/* Error message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-600/30 rounded-lg p-4 flex items-center">
            <div className="flex-1">
              <p className="text-red-700 dark:text-red-400">{error}</p>
            </div>
            <button
              onClick={() => dispatch(clearHelpError())}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            >
              ✕
            </button>
          </div>
        )}

        {/* Contact Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {contactOptions.map((option) => (
            <div
              key={option.id}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer transition-all"
            >
              <div className="flex items-center mb-3">
                <div className={`${option.color} p-3 rounded-lg mr-4`}>
                  <option.icon className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{option.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{option.availability}</p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300">{option.description}</p>
            </div>
          ))}
        </div>

        {/* Help Categories */}
        {loading && !displayCategories.length ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {displayCategories.map((category) => {
              const IconComponent = category.icon;
              return (
                <div
                  key={category.id}
                  className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer transition-all"
                >
                  <div className="flex items-center mb-4">
                    <IconComponent className="text-blue-500 text-2xl mr-3" />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {category.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">{category.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {(category.articles || []).map((article, index) => (
                      <li
                        key={index}
                        className="flex items-center text-gray-600 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400 cursor-pointer transition-colors"
                      >
                        <FaChevronRight className="mr-2 text-xs" />
                        {article}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {/* No results */}
        {displayCategories.length === 0 && searchQuery.trim() && !loading && (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">
              No results found for &ldquo;{searchQuery}&rdquo;
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                dispatch(clearSearch());
              }}
              className="mt-2 text-blue-500 hover:text-blue-600 underline"
            >
              Clear search
            </button>
          </div>
        )}

        {/* FAQ */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            {t('helpCenter.faqTitle')}
          </h2>
          <div className="space-y-4">
            {faqItems.map((faq) => (
              <div
                key={faq.id}
                className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0"
              >
                <button
                  className="w-full text-left flex items-center justify-between py-2 hover:text-blue-500 dark:hover:text-blue-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)}
                >
                  <span className="font-medium text-gray-900 dark:text-white">{faq.question}</span>
                  {expandedFAQ === faq.id ? (
                    <FaChevronDown className="text-gray-500 dark:text-gray-400" />
                  ) : (
                    <FaChevronRight className="text-gray-500 dark:text-gray-400" />
                  )}
                </button>
                {expandedFAQ === faq.id && (
                  <div className="mt-3 text-gray-600 dark:text-gray-300 leading-relaxed pl-2">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Feedback */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
            {t('helpCenter.wasHelpful')}
          </h3>
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => handleFeedback(true)}
              disabled={feedbackStatus === 'loading'}
              className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
            >
              <FaThumbsUp className="mr-2" /> {t('helpCenter.yesHelpful')}
            </button>
            <button
              onClick={() => navigate('/contact?type=support')}
              className="flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              <FaHeadset className="mr-2" /> {t('helpCenter.contactSupport')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenterPage;